import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import { Col, Container, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import {
  AxisDate,
  AxisDateTimeFull,
  AxisHoursWork,
  AxisTime,
} from "../util/AxisDateTime";
import {
  ButtonApproveTimesheet,
  ButtonEditTimesheet,
  ButtonRejectTimesheet,
  ButtonSubmitTimesheet,
} from "./ShiftShiftButtonActionsElements";
import PdfViewerModal from "../modals/PdfViewerModal";
import RejectTimesheetModal from "../modals/RejectTimesheetModal";
import ApproveTimesheetModal from "../modals/ApproveTimesheetModal";
import SubmitTimesheetModal from "../modals/SubmitTimesheetModal";
import { UserLink } from "../util/Links";
import { ShiftTableTitle } from "./ShiftTableTitle";
import { TimesheetStatusTextIcon } from "./TimesheetStatusTextIcon";

const ShiftTimesheetElement = ({ shift, updateShift, showActionButtons }) => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showRejectTimesheet, setShowRejectTimesheet] = useState(false);
  const [showApproveTimesheet, setShowApproveTimesheet] = useState(false);
  const [showSubmitTimesheet, setShowSubmitTimesheet] = useState(false);

  useEffect(() => {
    if (!shift?.bookedShiftId || !shift?.trust?.digitalInvoicing) return;
    setInvoiceLoading(true);
    authAxios().get(`/api/backoffice/invoice/by_booked_shift/${shift.bookedShiftId}`)
      .then((r) => setInvoice(r.data))
      .catch(() => setInvoice(null))
      .finally(() => setInvoiceLoading(false));
  }, [shift?.bookedShiftId]);

  const createInvoice = async () => {
    try {
      setCreatingInvoice(true);
      const response = await authAxios().post(
        `/api/backoffice/invoice/create_for_shift?booked_shift_id=${shift.bookedShiftId}`
      );
      setInvoice(response.data.invoice);
    } catch (error) {
      console.log(error);
      alert("Failed to create invoice");
    } finally {
      setCreatingInvoice(false);
    }
  };

  if (shift === null || shift === undefined) return;
  if (shift?.user?.id === null) return;
  if (shift?.digitalTimesheet === false) return;

  return (
    <Fragment>
      <ShiftTableTitle title={"Timesheet Information"} />
      <Table striped hover responsive>
        <tbody>
          <tr>
            <td>Status</td>
            <td className={"font-weight-bold"}>
              <TimesheetStatusTextIcon status={shift?.timesheet?.status} />
            </td>
          </tr>
          <tr>
            <td>Timesheet ID:</td>
            <td>{shift?.timesheet?.id}</td>
          </tr>

          <tr>
            <td>Timesheet Week:</td>
            <td>{AxisDate(shift?.timesheetWeek)}</td>
          </tr>

          {shift?.timesheet?.status === "REJECTED" ? (
            <tr>
              <td>Reject Reason:</td>
              <td>{shift?.timesheet?.rejectReason}</td>
            </tr>
          ) : null}

          {shift?.timesheet?.status === "APPROVED" ? (
            <tr>
              <td>Approved By:</td>
              <td>
                {AxisDateTimeFull(shift?.timesheet?.approvedOn)}
                {" by "}
                {UserLink(shift?.timesheet?.approvedBy)}
              </td>
            </tr>
          ) : null}

          <tr>
            <td>User</td>
            <td className={"font-weight-bold"}>{UserLink(shift?.user)}</td>
          </tr>
          <tr>
            <td>Timesheet File</td>
            <td className={"font-weight-bold"}>
              {shift?.timesheet === null ? (
                "Not Attached"
              ) : (
                <div>
                  <Button
                    className={"shift-button-approve icon-text-wrapper"}
                    onClick={() => setShowModal(true)}
                  >
                    {shift?.timesheet?.filename}
                  </Button>
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td>Start</td>
            <td className={"font-weight-bold"}>
              {shift?.timesheet === null
                ? "N/A"
                : AxisTime(shift?.timesheet?.start)}
            </td>
          </tr>
          <tr>
            <td>End</td>
            <td className={"font-weight-bold"}>
              {shift?.timesheet === null
                ? "N/A"
                : AxisTime(shift?.timesheet?.end)}
            </td>
          </tr>
          <tr>
            <td>Hours Worked</td>
            <td className={"font-weight-bold"}>
              {AxisHoursWork(shift?.timesheet?.hoursWorked)}
            </td>
          </tr>
        </tbody>
      </Table>

      <div className={"shift-button-container mt-2"}>
        <Container>
          <Row xs={"auto"} className={"justify-content-lg-center shift-button"}>
            {shift?.trust?.digitalInvoicing && !invoiceLoading && (
              invoice ? (
                <Col className={"g-3"}>
                  <Button
                    className={"shift-button-approve icon-text-wrapper"}
                    onClick={() => navigate(`/invoice/${invoice.id}`)}
                  >
                    View Invoice
                  </Button>
                </Col>
              ) : shift?.timesheet?.status === "APPROVED" ? (
                <Col className={"g-3"}>
                  <Button
                    className={"shift-button-new-shift icon-text-wrapper"}
                    onClick={createInvoice}
                    disabled={creatingInvoice}
                  >
                    {creatingInvoice ? "Creating…" : "Create Invoice"}
                  </Button>
                </Col>
              ) : null
            )}
            {showActionButtons === true && (
              <Fragment>
                <ButtonEditTimesheet
                  shift={shift}
                  action={() => setShowSubmitTimesheet(true)}
                />
                <ButtonSubmitTimesheet
                  shift={shift}
                  action={() => setShowSubmitTimesheet(true)}
                />
                <ButtonRejectTimesheet
                  shift={shift}
                  action={() => setShowRejectTimesheet(true)}
                />
                <ButtonApproveTimesheet
                  shift={shift}
                  action={() => setShowApproveTimesheet(true)}
                />
              </Fragment>
            )}
          </Row>
        </Container>
      </div>

      <SubmitTimesheetModal
        visible={showSubmitTimesheet}
        close={() => setShowSubmitTimesheet(false)}
        shift={shift}
        updateShift={updateShift}
      />
      <PdfViewerModal
        visible={showModal}
        close={() => setShowModal(false)}
        shift={shift}
      />
      <RejectTimesheetModal
        visible={showRejectTimesheet}
        close={() => setShowRejectTimesheet(false)}
        shift={shift}
        updateShift={updateShift}
      />
      <ApproveTimesheetModal
        visible={showApproveTimesheet}
        close={() => setShowApproveTimesheet(false)}
        shift={shift}
        updateShift={updateShift}
      />
    </Fragment>
  );
};

export default ShiftTimesheetElement;
